import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Paperclip } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { pdfAction } from '@/lib/pdf';
import { EMAIL_STATUS_LABEL, KIND_INFO, type SentEmail } from '@/lib/sales';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings/sent-emails')({ component: SentEmails });

interface Emails {
  transport: string;
  delivers: boolean;
  emails: SentEmail[];
}

/** Every email sent (or recorded) from a sales document, with a preview and the PDF. */
function SentEmails() {
  const orgId = useOrgId();
  const [openId, setOpenId] = useState<string | null>(null);
  const emails = useQuery({
    queryKey: ['sales', orgId, 'emails'],
    queryFn: () => api<Emails>(`/orgs/${orgId}/sales/emails`),
    enabled: Boolean(orgId),
  });
  const open = emails.data?.emails.find((e) => e.id === openId) ?? null;
  return (
    <>
      <PageHeader
        title="Sent emails"
        description={
          emails.data && !emails.data.delivers
            ? 'Invoices, credit notes, sales receipts and quotes emailed from Yedo Books. This server keeps each email here and does not deliver it.'
            : 'Invoices, credit notes, sales receipts and quotes emailed from Yedo Books.'
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : emails.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : emails.isError ? (
        <EmptyState title="Could not load sent emails" hint={emails.error.message} />
      ) : !emails.data.emails.length ? (
        <EmptyState
          title="No emails yet"
          hint="Use Send on an approved invoice, credit note, sales receipt or quote."
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">Date and time</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead className="w-[150px]">Document</TableHead>
                <TableHead className="w-[110px]">Status</TableHead>
                <TableHead className="w-[200px]">Attachment</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {emails.data.emails.map((e) => (
                <TableRow
                  key={e.id}
                  className="cursor-pointer"
                  data-testid="email-row"
                  onClick={() => setOpenId(e.id)}
                >
                  <TableCell className="tnum">{formatDateTime(e.createdAt)}</TableCell>
                  <TableCell>
                    {e.to}
                    {e.cc ? <div className="text-xs text-muted-foreground">Cc {e.cc}</div> : null}
                  </TableCell>
                  <TableCell>{e.subject}</TableCell>
                  <TableCell onClick={(ev) => ev.stopPropagation()}>
                    <DocumentLink e={e} />
                  </TableCell>
                  <TableCell>
                    <EmailStatus e={e} />
                  </TableCell>
                  <TableCell onClick={(ev) => ev.stopPropagation()}>
                    {e.attachmentName ? <Attachment orgId={orgId} e={e} /> : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={Boolean(open)} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="email-preview">
          {open ? (
            <>
              <DialogHeader>
                <DialogTitle>{open.subject}</DialogTitle>
                <DialogDescription>
                  {formatDateTime(open.createdAt)}
                  {open.sentBy ? ` · sent by ${open.sentBy}` : ''}
                </DialogDescription>
              </DialogHeader>
              <dl className="grid grid-cols-[80px_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-muted-foreground">From</dt>
                <dd>{open.from}</dd>
                <dt className="text-muted-foreground">To</dt>
                <dd>{open.to}</dd>
                {open.cc ? (
                  <>
                    <dt className="text-muted-foreground">Cc</dt>
                    <dd>{open.cc}</dd>
                  </>
                ) : null}
                <dt className="text-muted-foreground">Document</dt>
                <dd>
                  <DocumentLink e={open} />
                </dd>
                <dt className="text-muted-foreground">Status</dt>
                <dd>
                  <EmailStatus e={open} />
                  {open.status === 'recorded' ? (
                    <span className="text-muted-foreground"> (kept here, not delivered)</span>
                  ) : null}
                  {open.error ? <span className="text-destructive"> · {open.error}</span> : null}
                </dd>
              </dl>
              <div className="max-h-[320px] overflow-y-auto whitespace-pre-line rounded-md border bg-muted/30 p-3 text-sm">
                {open.body}
              </div>
              {open.attachmentName && orgId ? <Attachment orgId={orgId} e={open} /> : null}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function DocumentLink({ e }: { e: SentEmail }) {
  if (!e.documentId || !e.documentKind) return null;
  const info = KIND_INFO[e.documentKind];
  return (
    <Link className="text-primary hover:underline" to={`${info.path}/${e.documentId}`}>
      {info.label} {e.documentNumber}
    </Link>
  );
}

function EmailStatus({ e }: { e: SentEmail }) {
  return (
    <span
      className={cn(
        'rounded-full border px-2 py-0.5 text-xs font-medium',
        e.status === 'failed' ? 'border-destructive text-destructive' : 'text-muted-foreground',
      )}
    >
      {EMAIL_STATUS_LABEL[e.status]}
    </span>
  );
}

function Attachment({ orgId, e }: { orgId: string; e: SentEmail }) {
  const path = `/orgs/${orgId}/sales/emails/${e.id}/attachment`;
  return (
    <span className="inline-flex items-center gap-1 text-sm">
      <Paperclip className="size-4 text-muted-foreground" aria-hidden />
      <Button
        variant="link"
        size="sm"
        className="h-auto px-0"
        onClick={() => void pdfAction('preview', path, e.attachmentName!)}
      >
        {e.attachmentName}
      </Button>
      <Button
        variant="link"
        size="sm"
        className="h-auto px-1 text-muted-foreground"
        onClick={() => void pdfAction('download', `${path}?download=1`, e.attachmentName!)}
      >
        Download
      </Button>
    </span>
  );
}
